import React, { useEffect, useMemo, useState } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import { Modal } from '@atlassian/growth-pattern-library-modal';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { performGetRequestWithRetry } from '@atlassian/jira-fetch';
import { fireOperationalAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { userLimitModalQuery } from '@atlassian/jira-relay/src/__generated__/userLimitModalQuery.graphql';
import { useCloudId } from '@atlassian/jira-tenant-context-controller';
import type { Props } from './types';

export const UserLimitModal = ({ userCanUpgrade, upgradeUrl, userCount }: Props) => (
	<JSErrorBoundary
		id="userLimitModal"
		packageName="jiraLossAversion"
		teamName="luna-growth"
		fallback="unmount"
	>
		<UserLimitModalContent
			userCount={userCount}
			userCanUpgrade={userCanUpgrade}
			upgradeUrl={upgradeUrl}
		/>
	</JSErrorBoundary>
);

export const UserLimitModalContent = ({ userCanUpgrade, upgradeUrl, userCount }: Props) => {
	const [showModal, setShowModal] = useState(true);
	const onClose = () => setShowModal(false);

	const [userAvatars, setUserAvatars] = useState(null);
	const [loadingAvatars, setLoadingAvatars] = useState(true);
	const [avatarsError, setAvatarsError] = useState(null);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const cloudId = useCloudId();
	const data = useLazyLoadQuery<userLimitModalQuery>(
		graphql`
			query userLimitModalQuery($cloudId: ID!) {
				jira {
					allJiraProjects(
						cloudId: $cloudId
						filter: { types: SOFTWARE, sortBy: { order: ASC, sortBy: KEY } }
						first: 100
					) {
						edges {
							node {
								key
							}
						}
					}
				}
			}
		`,
		{ cloudId },
	);

	const projectKeys = useMemo(
		() => data?.jira?.allJiraProjects?.edges?.map((edge) => edge?.node?.key) || [],
		[data],
	);

	useEffect(() => {
		const fetchData = async () => {
			try {
				const usersResponse = await performGetRequestWithRetry(
					`/rest/api/3/user/assignable/multiProjectSearch?projectKeys=${projectKeys.join(',')}&maxResults=100`,
				);

				fireOperationalAnalytics(createAnalyticsEvent({}), 'fetched userAvatars', {});

				setUserAvatars(usersResponse);
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				fireErrorAnalytics({
					error,
					meta: {
						id: 'fetchUserAvatarsWithRetry',
						packageName: 'jiraLossAversion',
					},
				});
				setAvatarsError(error.message || 'Unknown avatar fetch error');
			} finally {
				setLoadingAvatars(false);
			}
		};

		fetchData();
	}, [projectKeys, createAnalyticsEvent]);

	if (!showModal) {
		return <></>;
	}

	return (
		<Modal
			title="PLACEHOLDER:"
			subtitle="PLACEHOLDER: subtitle"
			onClose={onClose}
			testId="loss-aversion.ui.loss-aversion-modal.user-limit"
			actions={
				<Button onClick={onClose} appearance="primary">
					Cancel
				</Button>
			}
		>
			{userCanUpgrade ? `can upgrade at link ${upgradeUrl}` : 'cant upgrade'}
			{loadingAvatars && <>Loading...</>}
			{avatarsError && <>Error: {avatarsError}</>}
			{!loadingAvatars && !avatarsError && (
				<>
					projectKeys: {projectKeys.join(',')}
					<br />
					userAvatars?: {!!userAvatars}
					<br />
					userCount: {userCount}
				</>
			)}
		</Modal>
	);
};
